import React, { useState, useEffect, useCallback } from 'react';
import { Star, CheckCircle, XCircle, Play, RefreshCw, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin Featured Quality Gate review queue. Lists every video creators have
// submitted for featuring (or admins have pulled into review) and lets the
// admin approve (→ marquee) or reject (→ stays published, not featured).
export const FeaturedQueueTab = ({ token }) => {
  const [queue, setQueue] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingOn, setActingOn] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/featured/queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setQueue(data.queue || []);
      setReasons(data.rejection_reasons || []);
    } catch (e) {
      toast.error('Failed to load featured queue');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const approve = async (video) => {
    setActingOn(video.id);
    try {
      const res = await fetch(`${API}/api/admin/videos/${video.id}/feature-approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Approved & featured: ${video.title}`);
      setQueue((q) => q.filter((v) => v.id !== video.id));
    } catch (e) {
      toast.error('Approve failed');
    } finally {
      setActingOn(null);
    }
  };

  const reject = async () => {
    if (!rejecting || !rejectReason) {
      toast.error('Pick a rejection reason');
      return;
    }
    setActingOn(rejecting.id);
    try {
      const res = await fetch(
        `${API}/api/admin/videos/${rejecting.id}/feature-reject?reason=${encodeURIComponent(rejectReason)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Rejected: ${rejecting.title}`);
      setQueue((q) => q.filter((v) => v.id !== rejecting.id));
      setRejecting(null);
      setRejectReason('');
    } catch (e) {
      toast.error('Reject failed');
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="featured-queue-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Star className="w-6 h-6 text-fuchsia-400" />
            Featured Review Queue
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Approve = video appears in marquee (Home/Discover top, landing).
            Reject = stays published in library, just not featured.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchQueue}
          disabled={loading}
          data-testid="featured-queue-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading && queue.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">Loading…</div>
      ) : queue.length === 0 ? (
        <div className="text-center py-16">
          <Star className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <h3 className="text-lg text-zinc-400 mb-1">Queue is empty</h3>
          <p className="text-zinc-500 text-sm">
            Creators can submit videos for review from their Studio. You can also pull a published video into review from the Videos tab.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="featured-queue-list">
          {queue.map((v) => (
            <div
              key={v.id}
              className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden"
              data-testid={`featured-queue-row-${v.id}`}
            >
              <div className="relative aspect-video bg-zinc-800">
                {/* Shared resolver covers Bunny videos (thumbnail_url,
                    custom_thumbnail_url) AND legacy MP4s (thumbnail_path).
                    The old hand-rolled check only handled http-prefixed
                    thumbnail_url and left everything else blank. */}
                <img
                  src={resolveThumbnailUrl(v)}
                  alt={v.title}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setPreviewVideo(v)}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity"
                  data-testid={`featured-queue-preview-${v.id}`}
                >
                  <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                    <Play className="w-6 h-6 text-black" />
                  </div>
                </button>
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-amber-500/90 text-black text-[10px] font-bold tracking-wide">
                  PENDING
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-white truncate">{v.title}</h3>
                <p className="text-xs text-zinc-500 truncate">
                  {v.artist_name || v.artist || '—'}
                </p>
                <p className="text-[11px] text-zinc-600 mt-1">
                  Submitted {v.submitted_for_featured_at
                    ? new Date(v.submitted_for_featured_at).toLocaleString()
                    : '—'}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    className="flex-1 bg-green-600 hover:bg-green-500 text-white"
                    onClick={() => approve(v)}
                    disabled={actingOn === v.id}
                    data-testid={`featured-queue-approve-${v.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={() => {
                      setRejecting(v);
                      setRejectReason('');
                    }}
                    disabled={actingOn === v.id}
                    data-testid={`featured-queue-reject-${v.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview modal */}
      {previewVideo && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewVideo(null)}
          data-testid="featured-queue-preview-modal"
        >
          <div
            className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-3xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={(previewVideo.file_url || previewVideo.video_url || '').startsWith('http')
                ? (previewVideo.file_url || previewVideo.video_url)
                : `${API}${previewVideo.file_url || previewVideo.video_url}`}
              controls
              autoPlay
              className="w-full aspect-video bg-black"
            />
            <div className="p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-white">{previewVideo.title}</h3>
                <p className="text-xs text-zinc-500">{previewVideo.artist_name || previewVideo.artist}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setPreviewVideo(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject reason modal */}
      {rejecting && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => !actingOn && setRejecting(null)}
          data-testid="featured-queue-reject-modal"
        >
          <div
            className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white mb-1">Reject from Featured</h3>
            <p className="text-sm text-zinc-500 mb-4">
              &ldquo;{rejecting.title}&rdquo; stays published in the library — it just won&apos;t appear in marquee spots.
            </p>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Reason</label>
            <Select value={rejectReason} onValueChange={setRejectReason}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="featured-queue-reject-reason">
                <SelectValue placeholder="Pick a reason…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                {reasons.map((r) => (
                  <SelectItem key={r} value={r} className="text-white">
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setRejecting(null)}
                disabled={!!actingOn}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-500 text-white"
                onClick={reject}
                disabled={!rejectReason || !!actingOn}
                data-testid="featured-queue-reject-confirm"
              >
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
